import { Stack } from '@mui/material';
import { portalPaths, UrlParams } from '@provision/portal-routes';
import { MapPin } from 'lucide-react';
import { useCallback, useState } from 'react';
import {
  MergeEntityCard,
  type MergeEntitySummary,
  MergeTargetPicker,
} from 'src/components/MergeTargetPicker';
import { ModalContainer } from 'src/components/ModalContainer';
import { Address, useMergeAddress } from 'src/graphql';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import { useNavigateRoute, useRouteParams } from 'src/utils/routing';
import { useSiteTargetSearch } from './use-site-target-search';

const SITE_ICON = <MapPin size={16} />;

const toSiteSummary = (address: Address): MergeEntitySummary => ({
  id: address.addressId,
  title: address.fullAddress ?? address.addressId,
  secondary: address.businessName ?? address.ownerFullName ?? undefined,
});

export const MergeSiteModal = () => {
  const { closeModal } = useModalMethods(Modals.MergeSiteModal);
  const { modalProps } = useModalState(Modals.MergeSiteModal);
  const [addressTarget, setAddressTarget] = useState<Address | null>(null);
  const { customViewId } = useRouteParams(UrlParams.customViewId);
  const { mergeAddress, loading } = useMergeAddress();

  const { address = null } = modalProps ?? {};

  const {
    records,
    loading: searchLoading,
    hasMore,
    loadMore,
    setSearchTerm,
  } = useSiteTargetSearch(address);

  const { showErrorAlert, showSuccessAlert } = useNotifications();

  const { navigate } = useNavigateRoute();

  const handleMergeSites = useCallback(async () => {
    if (!address?.addressId) {
      showErrorAlert(`Invalid address target ${address?.addressId}`);
      return;
    }

    if (!addressTarget?.addressId) {
      showErrorAlert(`Invalid address origin ${addressTarget?.addressId}`);
      return;
    }

    const response = await mergeAddress({
      addressOriginId: address.addressId,
      addressTargetId: addressTarget.addressId,
    });

    if (!response) {
      return;
    }

    if (response.errors) {
      showErrorAlert(
        (Array.isArray(response.errors)
          ? (response.errors[0] as { message?: string }).message!
          : (response.errors as { message?: string }).message!) ||
          'Something went wrong merging sites, please try again',
      );
      return;
    }

    showSuccessAlert('Sites were merged');
    navigate(portalPaths.site.full(addressTarget?.addressId), { state: { customViewId } });
    closeModal();
  }, [
    address?.addressId,
    addressTarget?.addressId,
    mergeAddress,
    showSuccessAlert,
    navigate,
    customViewId,
    closeModal,
    showErrorAlert,
  ]);

  return (
    <ModalContainer
      open
      size="medium"
      title="Merge sites"
      onClose={closeModal}
      actions={[
        { label: 'Cancel', kind: 'secondary' },
        {
          label: 'Merge sites',
          kind: 'primary',
          onClick: handleMergeSites,
          disabled: !address || !addressTarget,
          loading: !!loading,
          loadingLabel: 'Merging…',
          testId: 'merge-site-submit',
        },
      ]}
      data-testid="merge-site-modal"
    >
      <Stack gap={2}>
        {address ? (
          <MergeEntityCard
            label="From"
            icon={SITE_ICON}
            summary={toSiteSummary(address)}
            testId="merge-origin-card"
          />
        ) : null}
        <MergeTargetPicker
          label="Into"
          placeholder="Search sites"
          icon={SITE_ICON}
          records={records}
          target={addressTarget}
          toSummary={toSiteSummary}
          onTargetChange={setAddressTarget}
          onSearchChange={setSearchTerm}
          loading={searchLoading}
          hasMore={hasMore}
          loadMore={loadMore}
        />
      </Stack>
    </ModalContainer>
  );
};
